import { computed, toRef, Ref } from 'vue';
import { useGraphQLApi } from '@/components/Common/useGraphQLApi';
import {
  getChartOptions,
  graphTypeConfigMapper,
  generateSeries,
  anomalySeverityMapper,
  chartConfigMapper,
  getLocalTimeForGraphs,
} from '@/components/Common/graph.config';
import {
  GetChartOptionsParamType,
  GraphDataNamesEnum,
  AnomalyDataObjectType,
  LegendInfoType,
  GraphTypesEnum,
  GraphTypes,
  ParamsType,
  HeaderValuesType,
  Aggregation
} from '@/components/Common/graph.types';
import { useStoreData } from './useStoreData';
import { aggregationValue } from '@/utils/helper-function';

export function useGraphData(
  queryName: string,
  params: ParamsType,
  chartOptionsData: Ref<GetChartOptionsParamType>,
  graphType: Ref<GraphTypes>,
  axisLabels: { x: string; y: string },
  aggregation?: Ref<Aggregation>,
  // legendsList?: Ref<LegendInfoType[]>,
) {
  const aggregationVal = aggregation?.value
  const { data, error, makegraphQLCall, isLoading } = useGraphQLApi({
    queryName,
    params,
    initData: [],
    formatResp: (resp) => resp?.data?.data,
  });
  const { intervalTime, getTimeZoneOff } = useStoreData();

  const options = computed(() => {
    return getChartOptions(
      {
        ...chartOptionsData.value,
        axisLabels,
      },
      graphType.value,
    );
  });

  const getData = () => {
    switch (graphType.value) {
      case GraphTypesEnum.FORECAST_CHART:
        return formatGraphAPIResponse(
          data.value,
          graphType.value,
          getTimeZoneOff.value,
          aggregationVal
        );
      case GraphTypesEnum.FORECAST_WITH_ANOMALY:
        return formatGraphAPIResponse(
          data.value,
          graphType.value,
          getTimeZoneOff.value,
          aggregationVal
        );
      case GraphTypesEnum.LINE_CHART:
        return formatLineGraphAPIResponse(
          data.value,
          intervalTime,
          params?.condition.name,
        );
      case GraphTypesEnum.LINE_CHART_LABELLED:
        return formatLineGraphLabelledAPIResponse(data.value, intervalTime);
      default:
        return formatGraphAPIResponse(
          data.value,
          graphType.value,
          getTimeZoneOff.value,
          aggregationVal
        );
    }
  };

  return {
    data: toRef(() => getData().data),
    anomalyList: toRef(() => getData().anomalyList),
    headerValues: toRef(() => getData().headerValues),
    error,
    makegraphQLCall,
    isLoading,
    options,
  };
}


function formatGraphAPIResponse(
  resp,
  graphType,
  timeZoneOffset,
  aggregation
) {
  const seriesMapper = {};
  const headerValues: HeaderValuesType = {
    total: 0,
    avg: 0,
    perMin: 0,
    anomalies: resp?.anomalyCount,
  };
  const anomalyList: AnomalyDataObjectType[] = [];
  const chartItems = graphTypeConfigMapper[graphType];
  for (let i = 0; i < resp?.predictions?.length; i++) {
    let currResp = resp?.predictions[i];
    for (let j = 0; j < chartItems?.length; j++) {
      let currItem = chartItems[j];
      if (!seriesMapper[currItem]) {
        seriesMapper[currItem] = [];
      }
      if (currItem === GraphDataNamesEnum.UPPER_THRESHOLD) {
        seriesMapper[currItem].push({ 
          x: getLocalTimeForGraphs(
            currResp[GraphDataNamesEnum.TIMESTAMPS],
            timeZoneOffset,
          ),
          y: [
            aggregation?.req ? aggregationValue(currResp[GraphDataNamesEnum.LOWER_THRESHOLD] || 0, aggregation?.val, aggregation?.type) : currResp[GraphDataNamesEnum.LOWER_THRESHOLD] || 0,
            aggregation?.req ? aggregationValue(currResp[GraphDataNamesEnum.UPPER_THRESHOLD] || 0, aggregation?.val, aggregation?.type) : currResp[GraphDataNamesEnum.UPPER_THRESHOLD] || 0,
          ],
        });
      } else if (currItem in anomalySeverityMapper) {
        let yData = null;
        if (
          anomalySeverityMapper[currItem] ==
            currResp[GraphDataNamesEnum.SEVERITY] &&
          currResp[GraphDataNamesEnum.ANOMALY]
        ) {
          yData = aggregation?.req ? aggregationValue(currResp[GraphDataNamesEnum.GROUND_TRUTH], aggregation?.val, aggregation?.type) :currResp[GraphDataNamesEnum.GROUND_TRUTH];
          anomalyList.push({
            x: getLocalTimeForGraphs(
              currResp[GraphDataNamesEnum.TIMESTAMPS],
              timeZoneOffset,
            ),
            y: yData,
            [GraphDataNamesEnum.SEVERITY]: currResp[GraphDataNamesEnum.SEVERITY],
            [GraphDataNamesEnum.UPPER_THRESHOLD]:
              aggregation?.req ? aggregationValue(currResp[GraphDataNamesEnum.UPPER_THRESHOLD], aggregation?.val, aggregation?.type) :currResp[GraphDataNamesEnum.UPPER_THRESHOLD],
            [GraphDataNamesEnum.LOWER_THRESHOLD]:
              aggregation?.req ? aggregationValue(currResp[GraphDataNamesEnum.LOWER_THRESHOLD], aggregation?.val, aggregation?.type) :currResp[GraphDataNamesEnum.LOWER_THRESHOLD],
          });
        }

        seriesMapper[currItem].push({
          x: getLocalTimeForGraphs(
            currResp[GraphDataNamesEnum.TIMESTAMPS],
            timeZoneOffset,
          ),
          y: yData,
        });
      } else {
        seriesMapper[currItem].push({
          x: getLocalTimeForGraphs(
            currResp[GraphDataNamesEnum.TIMESTAMPS],
            timeZoneOffset,
          ),
          y: aggregation?.req ? aggregationValue(currResp[currItem], aggregation?.val, aggregation?.type) :currResp[currItem],
        });
      }
    }
  }

  return {
    data: generateSeries(seriesMapper, chartItems),
    anomalyList,
    headerValues: headerValues,
  };
}

function formatChartOptions(
  options: GetChartOptionsParamType,
  legendsList: LegendInfoType[] = [],
  graphType,
): GetChartOptionsParamType {
  // TODO: function will be removed post verification
  // options.colors = chartConfigMapper[graphType]?.colors?.filter(
  //   (_obj, i) => legendsList[i]?.isActive ?? true,
  // );
  // options.markersSize = chartConfigMapper[graphType]?.markersSize?.filter(
  //   (_obj, i) => legendsList[i]?.isActive ?? true,
  // );
  // options.strokeWidth = chartConfigMapper[graphType]?.strokeWidth?.filter(
  //   (_obj, i) => legendsList[i]?.isActive ?? true,
  // );
  // options.fillOpacity = chartConfigMapper[graphType]?.fillOpacity?.filter(
  //   (_obj, i) => legendsList[i]?.isActive ?? true,
  // );
  return options;
}

function formatLineGraphAPIResponse(res, intervalTime, metricName) {
  const headerValues: HeaderValuesType = {
    avg: res?.avg,
    total: res?.total,
    perMin: res?.perMin,
    anomalies: 0,
  };
  const serie = {
    name: metricName,
    type: 'line',
    data: res?.values?.values?.map((item, itemIndex) => ({
      x:
        typeof intervalTime.value[itemIndex] === 'undefined'
          ? ''
          : intervalTime.value[itemIndex],
      y: item.value,
    })),
  };

  return { data: [serie], anomalyList: [], headerValues: headerValues };
}

function formatLineGraphLabelledAPIResponse(res, intervalTime) {
  const headerValues: HeaderValuesType = {
    total: 0,
    avg: 0,
    perMin: 0,
    anomalies: 0,
  };
  const series = res?.map((obj, index) => {
    const serie = {
      name: obj.label,
      type: 'line',
      data: obj?.values?.values?.map((item, itemIndex) => ({
        x:
          typeof intervalTime.value[itemIndex] === 'undefined'
            ? ''
            : intervalTime.value[itemIndex],
        y: item.value,
      })),
    };
    return serie;
  });
  return { data: series || [], anomalyList: [], headerValues: headerValues };
}
